/*
 * <infinite-scroll data-section-id="...">
 *
 * Auto-loads the next page of products into the collection/search grid using
 * Shopify's Section Rendering API (?section_id=...&page=N), then appends the
 * returned [data-grid-item] nodes. Lives inside [data-products-grid] so that a
 * filter/sort replacement of the grid re-creates it and resets to page 1.
 *
 * Backfill: the grid can render fewer items than Shopify paginated for (e.g.
 * when the "Out of stock" filter is enforced at the product level and partially
 * in-stock products are skipped). A page that adds too few/zero items leaves the
 * sentinel in view, so after each load we keep fetching while the sentinel is
 * still within the load threshold. When pages are exhausted with an empty grid,
 * a no-results message is surfaced.
 */
(function () {
  if (window.customElements.get('infinite-scroll')) {
    return;
  }

  const LOAD_THRESHOLD = 600;

  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.exhausted = false;
      this.handleIntersect = this.handleIntersect.bind(this);
    }

    connectedCallback() {
      this.gridOuter = this.closest('[data-products-grid]');
      this.sectionId = this.dataset.sectionId;
      this.loader = this.querySelector('[data-infinite-loader]');
      if (!this.gridOuter || !this.sectionId) {
        return;
      }

      this.grid = this.gridOuter.querySelector('.grid');
      if (!this.grid) {
        return;
      }

      // Tell CSS that JS-driven infinite scroll is active (hides pagination).
      this.gridOuter.classList.add('is-infinite');

      this.nextPage = this.getNextPage();
      if (!this.nextPage) {
        // No further pages — the rendered grid is all there is. If the grid is
        // empty (everything was skipped), surface the empty state.
        this.exhausted = true;
        this.showEmptyStateIfNeeded();
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersect, {
        rootMargin: '0px 0px ' + LOAD_THRESHOLD + 'px 0px',
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    // Reads the "next" page number from the current pagination link.
    getNextPage() {
      const nextLink = this.gridOuter.querySelector('.pagination-custom__next[href]');
      if (!nextLink) {
        return null;
      }
      const url = new URL(nextLink.href, window.location.origin);
      const page = url.searchParams.get('page');
      return page ? parseInt(page, 10) : null;
    }

    handleIntersect(entries) {
      if (!entries[0].isIntersecting || this.loading) {
        return;
      }
      this.loadNextPage();
    }

    buildUrl() {
      const url = new URL(window.location.pathname, window.location.origin);
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.set('section_id', this.sectionId);
      params.set('page', String(this.nextPage));
      url.search = params.toString();
      return url.toString();
    }

    async loadNextPage() {
      this.loading = true;
      this.toggleLoader(true);

      try {
        const response = await fetch(this.buildUrl());
        if (!response.ok) {
          throw new Error('Section Rendering API returned ' + response.status);
        }
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const newItems = doc.querySelectorAll(
          '[data-products-grid] .grid > [data-grid-item]'
        );
        newItems.forEach((item) => this.grid.appendChild(item));

        const hasNext = doc.querySelector('.pagination-custom__next[href]');
        if (hasNext) {
          this.nextPage += 1;
        } else {
          this.exhausted = true;
          if (this.observer) {
            this.observer.disconnect();
          }
        }
      } catch (error) {
        // Stop auto-loading and reveal the numbered pagination so the shopper
        // can still navigate manually.
        console.warn('[infinite-scroll]', error);
        this.gridOuter.classList.remove('is-infinite');
        if (this.observer) {
          this.observer.disconnect();
        }
        this.toggleLoader(false);
        this.loading = false;
        return;
      }

      this.toggleLoader(false);
      this.loading = false;
      this.afterLoad();
    }

    // After a page loads, either keep backfilling (the sentinel is still in
    // view because too few items were appended) or surface the empty state.
    afterLoad() {
      if (this.exhausted) {
        this.showEmptyStateIfNeeded();
        return;
      }
      if (this.isWithinThreshold()) {
        this.loadNextPage();
      }
    }

    isWithinThreshold() {
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top <= viewportHeight + LOAD_THRESHOLD;
    }

    // Shows a no-results message when every page has loaded but no product made
    // it into the grid (e.g. the collection has no fully sold-out products).
    showEmptyStateIfNeeded() {
      if (!this.grid || this.grid.querySelector('[data-grid-item]')) {
        return;
      }
      if (this.grid.querySelector('[data-infinite-empty]')) {
        return;
      }

      const message = this.dataset.emptyText;
      if (!message) {
        return;
      }

      const empty = document.createElement('div');
      empty.className = 'no-results';
      empty.setAttribute('data-infinite-empty', '');
      const text = document.createElement('p');
      const strong = document.createElement('strong');
      strong.textContent = message;
      text.appendChild(strong);
      empty.appendChild(text);
      this.grid.appendChild(empty);
    }

    toggleLoader(show) {
      if (this.loader) {
        this.loader.hidden = !show;
        this.loader.setAttribute('aria-busy', show ? 'true' : 'false');
      }
    }
  }

  window.customElements.define('infinite-scroll', InfiniteScroll);
})();
